import React, { useEffect, useState } from 'react';
import { getDownloadFileSignedURL } from 'wasp/client/operations';
import { ExternalLink, FileText, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  getFoodCourtMenu,
  isImageMenu,
  isPdfMenu,
  isS3FoodCourtMenu,
  resolveFoodCourtMenuViewUrl,
} from './menuTypes';
import { normalizeWebsiteUrl } from '../../components/ui/detail-list-item';

type MenuViewerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  metadata: Record<string, unknown> | null | undefined;
};

export function MenuViewer({ open, onOpenChange, title = 'Menu', metadata }: MenuViewerProps) {
  const menu = getFoodCourtMenu(metadata);
  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !menu) {
      setViewUrl(null);
      setError(null);
      return;
    }

    let cancelled = false;

    async function resolveUrl() {
      setLoading(true);
      setError(null);
      try {
        const directUrl = resolveFoodCourtMenuViewUrl(menu!);
        if (directUrl) {
          const url = directUrl.startsWith('http') ? directUrl : directUrl;
          if (!cancelled) setViewUrl(url.startsWith('/') ? url : normalizeWebsiteUrl(url));
          return;
        }
        if (isS3FoodCourtMenu(menu!)) {
          const signed = await getDownloadFileSignedURL({ key: menu!.key! });
          if (!cancelled) setViewUrl(signed);
        }
      } catch {
        if (!cancelled) setError('Could not load menu. Try again or open in a new tab.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    resolveUrl();
    return () => {
      cancelled = true;
    };
  }, [open, menu?.key, menu?.url, menu?.storage]);

  if (!menu) return null;

  const displayName = menu.fileName || 'Menu';
  const showPdf = isPdfMenu(menu);
  const showImage = isImageMenu(menu) && !showPdf;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle className="flex items-center gap-2 pr-8">
            <FileText className="w-5 h-5 text-primary" />
            {title}
          </DialogTitle>
          <p className="text-sm text-muted-foreground truncate">{displayName}</p>
        </DialogHeader>

        <div className="flex-1 min-h-0 px-6 pb-6">
          {loading && (
            <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading menu…
            </div>
          )}

          {error && (
            <div className="text-center py-12 text-destructive text-sm">{error}</div>
          )}

          {!loading && !error && viewUrl && showImage && (
            <div className="flex justify-center overflow-auto max-h-[calc(90vh-8rem)] rounded-lg border bg-muted/30 p-2">
              <img
                src={viewUrl}
                alt={displayName}
                className="max-w-full h-auto object-contain"
              />
            </div>
          )}

          {!loading && !error && viewUrl && showPdf && (
            <iframe
              title={displayName}
              src={viewUrl}
              className="w-full h-[min(70vh,600px)] rounded-lg border bg-white"
            />
          )}

          {!loading && !error && viewUrl && !showImage && !showPdf && (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground mb-4">Preview not available for this file type.</p>
              <a
                href={viewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                Open menu
              </a>
            </div>
          )}
        </div>

        {viewUrl && (
          <div className="px-6 pb-4 shrink-0 border-t pt-3">
            <a
              href={viewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <ExternalLink className="w-4 h-4" />
              Open in new tab
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

type MenuViewerButtonProps = {
  metadata: Record<string, unknown> | null | undefined;
  itemTitle?: string;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
};

export function MenuViewerButton({
  metadata,
  itemTitle,
  className,
  onClick,
}: MenuViewerButtonProps) {
  const menu = getFoodCourtMenu(metadata);
  const [open, setOpen] = useState(false);

  if (!menu) {
    return <span className="text-sm text-gray-400">—</span>;
  }

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          onClick?.(e);
          e.stopPropagation();
          setOpen(true);
        }}
        className={
          className ??
          'text-sm font-medium text-primary hover:text-primary/80 hover:underline inline-flex items-center gap-1'
        }
      >
        <FileText className="w-3.5 h-3.5" />
        View menu
      </button>
      <MenuViewer
        open={open}
        onOpenChange={setOpen}
        title={itemTitle ? `${itemTitle} — Menu` : 'Menu'}
        metadata={metadata}
      />
    </>
  );
}
